import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  useGetLabReportEditQuery,
  useUploadLabReportMutation,
} from "../State/Api/GetallappointmentsApiSlice";
import PulseLoader from "./Utility/PulseLoader";
import { useFormik } from "formik";
import {
  ArrowLeft,
  Upload,
  Heart,
  Droplets,
  Weight,
  Ruler,
  Thermometer,
  ClipboardList,
  User,
  UserCheck,
} from "lucide-react";
import FilePreviewSwitcher from "./FilePreviewSwitcher";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from 'yup';
import { DatePicker } from "rsuite";


const EditPatientReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Assume patientUserUniqueID and appointmentID come in location state
 const { patientUserUniqueID, appointmentID } = useParams();

  // Fetch patient data for editing
  const {
    data: patients,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetLabReportEditQuery({ patientUserUniqueID, appointmentID });

  const [uploadReport] = useUploadLabReportMutation();

  // Store patient data from API response
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Selected test index for showing/editing the current test
  const [selectedTest, setSelectedTest] = useState(0);

  // Rich text editor state refs and commands
  const [activeCommands, setActiveCommands] = useState({});
  const editorRef = useRef(null);

  useEffect(() => {
    if (patients) {
      setSelectedPatient(patients);
      // Reset selected test index to 0 on new data
      setSelectedTest(0);
    }
  }, [patients]);

  const formatText = (cmd) => {
    document.execCommand(cmd, false, null);
    updateActiveCommands();
  };

  const updateActiveCommands = () => {
    const commands = [
      "bold",
      "italic",
      "underline",
      "justifyLeft",
      "justifyCenter",
      "justifyRight",
    ];
    const active = {};
    commands.forEach((cmd) => {
      active[cmd] = document.queryCommandState(cmd);
    });
    setActiveCommands(active);
  };

  const addedTestList =  selectedPatient?.testReports11?.map((test) => ({
      testName: test.testName || "",
      reportDate: test.reportDate ? test.reportDate.split("T")[0] : "",
      reportType: test.reportType || "",
      observationNote: test.observationNote || "",
      imageupload:
        test.reportFiles?.map((file) => ({
          formFile: null,
          reportName: file.reportName || "",
          fileName: file.fileName || "",
          fileUrl: file.fileName || "",
        })) || [],
        input: true
    })) || [];

    const pendingTestList = selectedPatient?.testnamesforpendingfiles?.map((test) => ({
      testName: test.testName || "",
      reportDate: test.reportDate ? test.reportDate.split("T")[0] : "",
      reportType: test.reportType || "",
      observationNote: test.observationNote || "",
      imageupload:
        test.reportFiles?.map((file) => ({
          formFile: null,
          reportName: file.reportName || "",
          fileName: file.fileName || "",
          fileUrl: file.fileName || "",
        })) || [],
        input: false
    })) || [];

    const prescribedTestList = [...addedTestList, ...pendingTestList];


  const toolbarButtons = [
    { cmd: "bold", label: <span className="font-bold">B</span> },
    { cmd: "italic", label: <span className="italic">I</span> },
    { cmd: "underline", label: <span className="underline">U</span> },
    { cmd: "justifyLeft", label: "⯇" },
    { cmd: "justifyCenter", label: "≡" },
    { cmd: "justifyRight", label: "⯈" },
    { cmd: "undo", label: "↺" },
    { cmd: "redo", label: "↻" },
  ];

      const [savedTests, setSavedTests] = useState([]);


const validationSchema = Yup.object({
  uploadfiles: Yup.array().of(
    Yup.object({
      testName: Yup.string().required('Test Name is required'),
      reportDate: Yup.string().required('Report Date is required'),
      reportType: Yup.string().required('Report Type is required'),
      observationNote: Yup.string().required('Observation Note is required'),
      imageupload: Yup.array().min(1, "Please add the relevant file, save and then Submit the report.")
    })
  ).min(1, 'You must save atleast one report befor submitting.')
})

  // Initialize Formik with data from selectedPatient and prescribedTestList
const formik = useFormik({
initialValues: {
      PatientUserUniqueID: selectedPatient?.patientInfo11?.userUniqueID || "",
      DoctorUserUniqueID:
        selectedPatient?.patientInfo11?.doctorUserUniqueID || "",
      UserClinicID: localStorage.getItem("userClinicID") || "",
      IsActive: true,
      UpdatedBy: "string",
      AppointmentID: selectedPatient?.patientInfo11?.appointmentID || 0,
      uploadfiles: prescribedTestList,
    },
  // Disable automatic validation
  validateOnChange: false,
  validateOnBlur: false,
  onSubmit: async (values, { setSubmitting }) => {
    // Your existing submission logic here
    const formdata = new FormData();
    const selectedTestData = values.uploadfiles[selectedTest];
    
    formdata.append("PatientUserUniqueID", values.PatientUserUniqueID);
    formdata.append("DoctorUserUniqueID", values.DoctorUserUniqueID);
    formdata.append("UserClinicID", values.UserClinicID);
    formdata.append("IsActive", values.IsActive);
    formdata.append("UpdatedBy", values.UpdatedBy);
    formdata.append("AppointmentID", values.AppointmentID);

   if (savedTests.length > 0) {
  savedTests.forEach((test, index) => {
    formdata.append(`uploadfiles[${index}].testName`, test.testName);

    const formattedDate = test.reportDate
  ? new Date(test.reportDate).toLocaleDateString("en-CA")
  : "";
    formdata.append(`uploadfiles[${index}].reportDate`, formattedDate);

    formdata.append(`uploadfiles[${index}].reportType`, test.reportType);
    formdata.append(`uploadfiles[${index}].observationNote`, test.observationNote);

    test.imageupload.forEach((image, idx) => {
      if (image.formFile) {
        formdata.append(`uploadfiles[${index}].imageupload[${idx}].formFile`, image.formFile);
      } else if (image.fileName) {
        formdata.append(`uploadfiles[${index}].imageupload[${idx}].fileName`, image.fileName);
      }
      formdata.append(`uploadfiles[${index}].imageupload[${idx}].reportName`, image.reportName);
    });
  });
} else {
  formdata.append("uploadfiles", JSON.stringify([]));
}

    try {
      const result = await uploadReport(formdata).unwrap();
      if (result?.result_Code === 100) {
        toast.success("Submitted successfully", { autoClose: 3000 });
        setTimeout(() => {
          navigate("/PatientReports");
        }, 2000);
      } else if (result?.result_Code === 200) {
        toast.success("Submitted successfully", { autoClose: 3000 });
        setTimeout(() => {
          navigate("/PatientReports");
        }, 2000);
      } else {
        toast.warning("Please Save the report before submitting");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  },
  enableReinitialize: true
});

const handleFormSubmit = async (e) => {
  e.preventDefault();
  
  try {
    console.log(formik.values);
    // Manually trigger validation
    await validationSchema.validate({...formik.values, uploadfiles: [...savedTests]}, { abortEarly: false });
    
    // If validation passes, proceed with form submission
    formik.handleSubmit(e);
  } catch (validationErrors) {
    // Show validation errors as toasts
    if (validationErrors.inner && validationErrors.inner.length > 0) {
      validationErrors.inner.forEach(error => {
        toast.error(error.message);
      });
    } else {
      toast.error(validationErrors.message);
    }
    
    // Set form errors and touched state to show field-level errors too
    formik.setErrors(validationErrors.inner.reduce((acc, error) => {
      acc[error.path] = error.message;
      return acc;
    }, {}));
    
    // Touch all fields to show errors
    formik.setTouched(
      validationErrors.inner.reduce((acc, error) => {
        acc[error.path] = true;
        return acc;
      }, {})
    );
  }
};

    //   const handleSaveTest = (test) => {
    //   const testToSave = formik.values.uploadfiles[selectedTest];
    //   if(savedTests.some((savedTest) => savedTest.testName === testToSave.testName)){
    //     savedTests[savedTests.findIndex((savedTest) => savedTest.testName === testToSave.testName)] = testToSave;
    //   }else{
    //     setSavedTests((prevTests) => [...prevTests, testToSave]);
    //   }
      
    // };
    const [saving, setSaving] = useState(false);

const handleSaveTest = () => {
  setSaving(true);
  const testToSave = formik.values.uploadfiles[selectedTest];

  setSavedTests((prevTests) => {
    const updatedTests = [...prevTests];
    const existingIndex = updatedTests.findIndex(
      (savedTest) => savedTest.testName === testToSave.testName
    );

    if (existingIndex !== -1) {
      updatedTests[existingIndex] = testToSave;
    } else {
      updatedTests.push(testToSave);
    }

    return updatedTests;
  });

  setTimeout(() => {
    setSaving(false); // Simulate async save delay
  }, 800); // Optional: simulate delay for UX
};



  // Function to add an empty report file in current selected test
  const addReportFile = () => {
    const currentFiles =
      formik.values.uploadfiles[selectedTest]?.imageupload || [];
    const updatedFiles = [...currentFiles, { formFile: null, reportName: "", fileName:  "", fileUrl: "", fileType : "" }];
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload`,
      updatedFiles
    );
  };

  // Handle selected test change (index)
  const handleTestChange = (e) => {
    const index = parseInt(e.target.value, 10);
    if (
      !isNaN(index) &&
      index >= 0 &&
      index < formik.values.uploadfiles.length
    ) {
      setSelectedTest(index);
      // Optional: reset editor content or other state as needed
      if (editorRef.current) {
        editorRef.current.innerHTML =
          formik.values.uploadfiles[index].observationNote || "";
      }
    }
  };

  // Handle file input change for imageupload in a test report
  const handleFileChange = (e, fileIndex) => {
    const file = e.target.files[0];
  
    if (!file) return;
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].formFile`,
      file
    );
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].fileName`, file.name
    )
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].fileUrl`, URL.createObjectURL(file)
    )
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].fileType`, file.type.split("/")[1]
    )
  };

  // Update editor content on selected test change
useEffect(() => {
  if (
    editorRef.current &&
    formik.values.uploadfiles?.[selectedTest]?.observationNote !== undefined
  ) {
    editorRef.current.innerHTML =
      formik.values.uploadfiles[selectedTest].observationNote || "";
  }
}, [selectedTest]);

useEffect(() => {
  if (
    editorRef.current &&
    formik.values.uploadfiles?.[selectedTest]?.observationNote !== undefined
  ) {
    editorRef.current.innerHTML =
      formik.values.uploadfiles[selectedTest].observationNote || "";
  }

  // Do not modify this dependancy array. This is what keeps that dumb editor from weirdly updating its inner html when the text changes.
}, [patients, formik.values.PatientUserUniqueID, appointmentID]);

const [previewFile, setPreviewFile] = useState(null);
const [type, setFileType] = useState(null);

  const closePreview = () => setPreviewFile(null);

useEffect(()=>{
  const getFileType = (filename, fileType) => {
  if(filename && ["jpg", "jpeg", "png", "webp", "gif", "bmp","pdf"].includes(filename.split(".").pop().toLowerCase())){
    const ext = filename.split(".").pop().toLowerCase();
  
  if (ext === "pdf") return { type: "pdf", label: "PDF", bg: "bg-red-100", text: "text-red-600" };
  if (["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext))
    return { type: "image", label: "IMG", bg: "bg-blue-100", text: "text-blue-600" };
  }
  else if(fileType !== "") {
    if (fileType === "pdf") return { type: "pdf", label: "PDF", bg: "bg-red-100", text: "text-red-600" };
  if (["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(fileType))
    return { type: "image", label: "IMG", bg: "bg-blue-100", text: "text-blue-600" };
  }
  return { type: "other", label: "FILE", bg: "bg-gray-200", text: "text-gray-600" };
};

  if(previewFile){
    const fileType = getFileType(previewFile.fileUrl, previewFile.fileType);
    setFileType(fileType);
  }
}, [previewFile]);


  if (isLoading || isFetching) {
    return (
      <div
        style={{
          height: "100vh", // Full viewport height
          display: "flex", // Use flexbox
          justifyContent: "center", // Center horizontally
          alignItems: "center", // Center vertically
        }}
      >
        <PulseLoader />
      </div>
    );
  }

  if (!isSuccess) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "red",
          fontWeight: "bold",
          fontSize: "1.2rem",
        }}
      >
        Failed to load patient data or no data available.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleFormSubmit}
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4"
    >
      <style>{`
        .card-modern {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(229, 231, 235, 0.6);
          transition: all 0.3s ease;
        }
        .card-modern:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        .vitals-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }
        .vital-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(229, 231, 235, 0.6);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .vital-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #10b981, #059669);
        }
        .vital-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        .rich-editor {
          min-height: 200px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          background-color: white;
          transition: border-color 0.3s ease;
          outline: none;
          overflow-y: auto;
        }
        .rich-editor:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        .file-upload-area {
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 24px;
          text-align: center;
          transition: all 0.3s ease;
          background: #fafafa;
          cursor: pointer;
        }
        .file-upload-area:hover {
          border-color: #10b981;
          background: #f0fdf4;
        }
        .file-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: all 0.3s ease;
        }
        .file-item:hover {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        .test-selector {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 12px 16px;
          transition: all 0.3s ease;
          font-weight: 500;
          width: 100%;
        }
        .test-selector:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        .gradient-button {
          background: linear-gradient(90deg, #10b981, #059669);
          color: white;
          font-weight: 600;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: background 0.3s ease;
        }
        .gradient-button:hover {
          background: linear-gradient(90deg, #059669, #047857);
        }

        .rs-uploader-picture-text .rs-uploader-file-item {
    border: 1px solid #e5e5ea;
    border: 1px solid var(--rs-border-primary);
    border-radius: 6px;
    height: 36px;
    margin-top: 10px;
    overflow: hidden;
    padding-left: 50px;
    padding-right: 30px;
    position: relative;
    -webkit-transition: background-color .3s linear;
    transition: background-color .3s linear;
}
    .rs-input-group {
            border-radius: 8px !important;
            border: #e5e5ea solid 1px !important;
            height: 40px;
            overflow: hidden;
            background: white;
          }
          
          .rs-input-group .rs-input {
            border-radius: 5px !important;
            border: none !important;
            background: none !important;
            box-shadow: none !important;
          }
          
          .rs-input-group .rs-input:focus {
            outline: none;
            box-shadow: none;
          }
          
          .rs-picker-toggle {
            border-radius: 8px !important;
          }
          
          .rs-input-group-inside .rs-input {
            border: none !important;
            font-size: 14px;
            border-radius: 8px !important;
            box-shadow: none !important;
            background: none !important;
          }
      `}</style>

      {/* Header */}
      <div className="max-w-9xl mx-auto mb-8">
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate("/PatientReports")}
            className="flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Edit Patient Report
            </h1>
            <p className="text-gray-600">
              Update patient reports and observations with detailed
              documentation.
            </p>
          </div>
        </div>
        <div className="h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-9xl mx-auto">
        <div className="lg:col-span-3 space-y-8">
          {/* Patient Vitals Section */}
          <div className="card-modern p-6">
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: "linear-gradient(to right, #3b82f6, #2563eb)",
                }}
              >
                <Heart className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">
                Patient Vitals
              </h2>
            </div>

            <div className="vitals-grid">
              <div className="vital-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Blood Group
                  </span>
                  <Droplets className="w-5 h-5 text-red-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedPatient?.medicalInfo11?.bloodGroup || "Not recorded"}
                </div>
              </div>
              <div className="vital-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Blood Pressure
                  </span>
                  <Heart className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedPatient?.medicalInfo11?.bloodPressure ||
                    "Not recorded"}
                </div>
              </div>
              <div className="vital-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Weight
                  </span>
                  <Weight className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedPatient?.medicalInfo11?.weight
                    ? `${selectedPatient.medicalInfo11.weight} Kg`
                    : "Not recorded"}
                </div>
              </div>
              <div className="vital-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Height
                  </span>
                  <Ruler className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedPatient?.medicalInfo11?.height
                    ? `${selectedPatient.medicalInfo11.height} cm`
                    : "Not recorded"}
                </div>
              </div>
              <div className="vital-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Pulse
                  </span>
                  <Heart className="w-5 h-5 text-pink-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedPatient?.medicalInfo11?.pulse
                    ? `${selectedPatient.medicalInfo11.pulse} bpm`
                    : "Not recorded"}
                </div>
              </div>
              <div className="vital-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Temperature
                  </span>
                  <Thermometer className="w-5 h-5 text-orange-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedPatient?.medicalInfo11?.temperature
                    ? `${selectedPatient.medicalInfo11.temperature} °C`
                    : "Not recorded"}
                </div>
              </div>
            </div>
          </div>

          {/* Test Observation Section */}
          <div className="card-modern p-6">
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: "linear-gradient(to right, #22c55e, #16a34a)",
                }}
              >
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">
                Test Observations
              </h2>
<button
  type="button"
  onClick={handleSaveTest}
  className="gradient-button text-sm flex items-center gap-2 px-4 py-2 rounded ml-auto min-w-[120px]"
  disabled={saving}
>
  {saving ? (

     "Saving..."
  ) : (
    "Save Report"
  )}
</button>
            </div>

            {/* Test Selector */}
            <div className="mb-8">
              <label
                htmlFor="testSelector"
                className="block text-sm font-medium text-gray-700 mb-3"
              >
                Select Test
              </label>
              <select
                id="testSelector"
                className="test-selector text-sm"
                value={selectedTest}
                onChange={handleTestChange}
              >
                {formik.values.uploadfiles.length === 0 ? (
                  <option value="">No tests available</option>
                ) : (
                  formik.values.uploadfiles.map((test, idx) => (
                    <option key={idx} value={idx}>
                      {test.testName || `Test ${idx + 1}`}
                    </option>
                  ))
                )}
              </select>
            </div>

            {formik.values.uploadfiles.length > 0 && (
              <>
                {/* Test Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Name
                    </label>
                    <input
                      type="text"
                      name={`uploadfiles.${selectedTest}.testName`}
                      className="w-full p-2 text-sm border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                      value={formik.values.uploadfiles[selectedTest].testName}
                      onChange={formik.handleChange}
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Report Date
                    </label>
                    <DatePicker
  name={`uploadfiles.${selectedTest}.reportDate`}
  className="w-full"
  style={{
    width: '100%'
  }}
  value={formik.values.uploadfiles[selectedTest].reportDate 
    ? new Date(formik.values.uploadfiles[selectedTest].reportDate) 
    : null}
  onChange={(date) => {
    formik.setFieldValue(`uploadfiles.${selectedTest}.reportDate`, date);
  }}
  onBlur={() => {
    formik.setFieldTouched(`uploadfiles.${selectedTest}.reportDate`, true);
  }}
  format="dd-MMM-yyyy"
  placeholder="Select report date"
  cleanable={true}
  size="md"
  appearance="default"
  block={true}
/>
                  </div>
                </div>

                {/* Report Type */}
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Type
                  </label>
                  <select
                    name={`uploadfiles.${selectedTest}.reportType`}
                    className="w-full p-2 text-sm border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                    value={formik.values.uploadfiles[selectedTest].reportType}
                    onChange={formik.handleChange}
                    disabled={formik.values.uploadfiles[selectedTest].input}
                  >
                    <option value="">Select Report Type</option>
                    <option value="Blood Test">Blood Test</option>
                    <option value="X-Ray">X-Ray</option>
                    <option value="MRI">MRI</option>
                    <option value="CT Scan">CT Scan</option>
                  </select>
                </div>

                {/* Observation Notes with toolbar */}
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Observation Notes
                  </label>
                  <div className="flex flex-wrap gap-2 mb-4 p-2">
                    {toolbarButtons.map(({ cmd, label }) => (
                      <button
                        key={cmd}
                        type="button"
                        className={`px-3 py-2 text-sm rounded-md border transition-all duration-200 ${
                          activeCommands[cmd]
                            ? "bg-green-500 text-white border-green-500"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                        onClick={() => formatText(cmd)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* <div
    ref={editorRef}
    className="rich-editor text-sm rounded-t-none relative"
    contentEditable
    onInput={(e) => {
      const html = e.currentTarget.innerHTML;
      formik.setFieldValue(
        `uploadfiles[${selectedTest}].observationNote`,
        html
      );
      updateActiveCommands();
    }}
    style={{ minHeight: "200px" }}
    suppressContentEditableWarning={true}
   
  /> */}

<div
ref={editorRef}
  dir="ltr"
  contentEditable
  className="rich-editor text-sm rounded-t-none relative min-h-[200px] border p-2 bg-white"
  onInput={(e) => {
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].observationNote`,
      e.currentTarget.innerHTML
    );
  }}
  suppressContentEditableWarning={true}
/>
                </div>

                {/* Report Files Upload */}
                 <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-lg font-bold text-gray-800">
                        Report Files
                      </label>
                      <button
                        type="button"
                        onClick={addReportFile}
                        className="gradient-dark-button text-sm flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Add File
                      </button>
                    </div>

                    {formik.values.uploadfiles[selectedTest]?.imageupload.length === 0 ? (
                      <div className="file-upload-area" onClick={addReportFile}>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <p className="text-base text-gray-600 font-medium">
                          Click to add report files
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Support for PDF, Images, and Word documents
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formik.values.uploadfiles[selectedTest]?.imageupload.map((file, index) => {
                          console.log(formik.values.uploadfiles[selectedTest]?.input)
                          return <div key={file.id} className="file-item">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Report Title
                                </label>
                                <input
                                  type="text"
                                  className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                                  placeholder="Enter report title"
                                  name={`uploadfiles[${selectedTest}].imageupload[${index}].reportName`}
                                  value={formik.values.uploadfiles[selectedTest]?.imageupload[index].reportName}
                                  disabled={formik.values.uploadfiles[selectedTest]?.input}
                                  onChange={(e)=>{
                                    formik.setFieldValue(`uploadfiles[${selectedTest}].imageupload[${index}].reportName`, e.target.value);
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Upload File
                                </label>
                                
                                <FilePreviewSwitcher fileUrl={file.fileUrl}
                                fileType={file.fileType}
                                setPreviewFile={setPreviewFile}
  fileName={file.fileName}
  onFileChange={(e) => handleFileChange(e, index)} />
                              </div>
                            </div>

                            
                          </div>
                        })}
                      </div>
                    )}
                  </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate("/PatientReports")}
                className="px-3 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300"
                style={{
                  width: "7rem",
                  height: "3rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Cancel
              </button>

              {formik.values.uploadfiles.length > 0 && (
                <button
                  type="submit"
                  className="gradient-button"
                  style={{
                    width: "7rem",
                    height: "3rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="lg:col-span-1">
          {/* Patient Info Card */}
          <div className="card-modern p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-600 text-white font-bold text-xl">
                {selectedPatient?.patientInfo11?.fullName?.slice(0, 1) || "P"}
              </div>
              <div>
                <h3 className="font-bold text-gray-900">
                  {selectedPatient?.patientInfo11?.fullName || "Patient Name"}
                </h3>
                <p className="text-sm text-gray-600">
                  Age: {selectedPatient?.patientInfo11?.age || "N/A"} • Gender:{" "}
                  {selectedPatient?.patientInfo11?.gender || "N/A"} • Phone:{" "}
                  {selectedPatient?.patientInfo11?.contactNumber || "N/A"}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">
                  Allergies
                </label>
                <p className="text-sm text-gray-700">
                  {selectedPatient?.medicalInfo11?.allergies_ifany &&
                  selectedPatient.medicalInfo11.allergies_ifany !== "None"
                    ? selectedPatient.medicalInfo11.allergies_ifany
                    : "None"}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">
                  Existing Conditions
                </label>
                <p className="text-sm text-gray-700">
                  {selectedPatient?.medicalInfo11?.existingMedicalConditions &&
                  selectedPatient.medicalInfo11.existingMedicalConditions !==
                    "None"
                    ? selectedPatient.medicalInfo11.existingMedicalConditions
                    : "None"}
                </p>
              </div>
            </div>
          </div>

          {/* Doctor Info Card */}
          <div className="card-modern p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-600 text-white font-bold text-xl">
                D
              </div>
              <h3 className="font-bold text-gray-900">Doctor Information</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">
                  Doctor Name
                </label>
                <p className="text-sm text-gray-700">
                  {selectedPatient?.patientInfo11?.doctorName || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">
                  Specialization
                </label>
                <p className="text-sm text-gray-700">
                  {selectedPatient?.patientInfo11?.specialization || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
            {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-4xl mx-4 rounded-xl shadow-lg relative">
            <button
              onClick={closePreview}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-2xl"
            >
              &times;
            </button>

            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-800">{previewFile.fileName}</h2>
              <div className="border rounded overflow-hidden max-h-[75vh]">
                {type?.type === "image" ? (
                  <img
                    src={previewFile.fileUrl}
                    alt={previewFile.fileName}
                    className="w-full object-contain max-h-[70vh] mx-auto"
                  />
                ) : type?.type === "pdf" ? (
                  <iframe
                    src={previewFile.fileUrl}
                    title={previewFile.fileName}
                    className="w-full h-[70vh]"
                  />
                ) : (
                  <p className="text-gray-500 p-4 text-center">Preview not supported</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
              <ToastContainer position="top-right" />
    </form>
  );
};

export default EditPatientReport;
